import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ChartBarIcon, DocumentChartBarIcon, AcademicCapIcon, ArrowLeftIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../context/AuthContext';
import { apiService } from '../../../services/apiService';

interface NavigationItem {
  id: string;
  name: string;
  path: string;
  icon: React.ComponentType<any>;
  description: string;
  badge?: string;
}

interface ClassInfo {
  id: string;
  name: string;
  code: string;
  term: string;
}

const ClassNavigation = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const [, setClassInfo] = useState<ClassInfo | null>(null);
  const [studentCount, setStudentCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadClassData = async () => {
      if (!classId || !user) return;

      try {
        const { data: classData } = await apiService.getClassById(classId);
        if (classData) {
          setClassInfo({
            id: classData.id,
            name: classData.name,
            code: classData.code,
            term: classData.term,
          });
        }
        
        // TODO: Replace with actual student count from database
        setStudentCount(25); // Placeholder
      } catch (error) {
        console.error('Failed to load class data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadClassData();
  }, [classId, user]);

  const handleGoToAllClasses = () => {
    if (profile?.userType === 'professor' || profile?.user_type === 'professor') {
      navigate('/dashboard');
    } else {
      navigate('/student-dashboard');
    }
  };

  const navigationItems: NavigationItem[] = [
    {
      id: 'overview',
      name: 'Overview',
      path: `/dashboard/class/${classId}`,
      icon: AcademicCapIcon,
      description: 'Class overview and quick actions'
    },
    {
      id: 'insights',
      name: 'Classroom Insights',
      path: `/dashboard/class/${classId}/insights`,
      icon: ChartBarIcon,
      description: 'Analytics and student data'
    },
    {
      id: 'students',
      name: 'Student Profiles',
      path: `/dashboard/class/${classId}/students`,
      icon: UsersIcon,
      description: 'Student profiles and management',
      badge: studentCount > 0 ? `${studentCount}` : undefined
    },
    {
      id: 'feedback',
      name: 'Feedback',
      path: `/dashboard/class/${classId}/feedback`,
      icon: DocumentChartBarIcon,
      description: 'Feedback management and reports'
    }
  ];

  const isActive = (path: string) => {
    // For exact matches or when the current path is a child of the nav path
    return location.pathname === path || 
           (path.includes('/insights') && location.pathname.includes('/insights')) ||
           (path.includes('/students') && location.pathname.includes('/students')) ||
           (path.includes('/feedback') && location.pathname.includes('/feedback')) ||
           (path === `/dashboard/class/${classId}` && location.pathname === `/dashboard/class/${classId}`);
  };

  if (loading) {
    return (
      <div className="bg-white border-b border-gray-200 mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="animate-pulse flex space-x-4">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-4 bg-gray-200 rounded w-20"></div>
            <div className="h-4 bg-gray-200 rounded w-28"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-b border-gray-200 mb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Simple Navigation */}
        <nav className="flex space-x-8">
          {/* All Classes Link */}
          <button
            type="button"
            onClick={handleGoToAllClasses}
            className="flex items-center py-4 px-2 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium text-sm transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            All Classes
          </button>
          
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <button
                type="button"
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`flex items-center py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  active
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5 mr-2" />
                {item.name}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default ClassNavigation; 